// =============================================================================
// VulnTracker — Axios API Client (Multi-Tenant)
// =============================================================================
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';

// In production VITE_API_URL is the Railway backend URL (e.g. https://your-app.railway.app)
// In dev it is empty so the Vite proxy handles /api → localhost:8001
const BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * Extract the tenant slug from the current hostname.
 *   siemens.vi.securusmind.com  →  "siemens"
 *   demo.vi.securusmind.com     →  "demo"
 *   localhost                   →  "demo"  (fallback)
 */
function getTenantSlug(): string {
  const hostname = window.location.hostname.toLowerCase();
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'demo';
  }
  const parts = hostname.split('.');
  // Expected: {tenant}.vi.securusmind.com
  if (parts.length >= 4 && parts[parts.length - 3] === 'vi') {
    return parts[0];
  }
  // Fallback: {tenant}.securusmind.com
  if (parts.length >= 3) {
    const slug = parts[0];
    return slug === 'vi' ? 'demo' : slug;
  }
  return 'demo';
}

const api = axios.create({
  baseURL: `${BASE}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor — attach JWT token and tenant header
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Always send tenant slug so the backend can resolve even behind proxies
    config.headers['X-Tenant-ID'] = getTenantSlug();
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor — handle 401 and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = useAuthStore.getState().refreshToken;
      if (refreshToken) {
        try {
          // Use the `api` instance (not raw axios) so X-Tenant-ID is attached
          const response = await api.post('/auth/refresh', {
            refresh_token: refreshToken,
          });
          const { access_token } = response.data;
          useAuthStore.getState().setTokens(access_token, refreshToken);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch {
          useAuthStore.getState().logout();
          window.location.href = '/login';
        }
      } else {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  },
);

export default api;
